import type { FillInput, Graphics } from 'pixi.js';
import type { DecorId } from '../../data/types';
import { mixColor, parseHexColor } from '../palette';
import { lookup } from '../util';

const TAU = Math.PI * 2;
const OUTLINE = parseHexColor('#2b2010');
const CREAM = parseHexColor('#fff8ea');

// What an exit's drawing moves with: beats since the sunrise, the kick's envelope, the share of the
// two bars of departure, the exit's turn (+x points out of the arena), and the frame's colours.
export interface ExitMoment {
  readonly beats: number;
  readonly kick: number;
  readonly calm: boolean;
  readonly depart: number;
  readonly turn: number;
  readonly ahead: number;
  readonly gold: number;
}

interface Motif {
  readonly beam: string;
  readonly width: number;
  readonly lights: readonly string[];
  draw(g: Graphics, post: number, reach: number): void;
}

const MOTIFS = new Map<DecorId, Motif>(
  Object.entries({
    pumpui: {
      beam: '#c8363a',
      width: 14,
      lights: ['#c8363a', '#d9a441', '#3b4cc0'],
      draw(g, post, reach) {
        for (const side of [-1, 1]) {
          for (const along of [0.35, 0.7]) {
            const y = side * post * along;
            g.poly([reach * 0.2 - 6, y - 6, reach * 0.2 - 6, y + 6, reach * 0.2 + 6, y]).fill(
              parseHexColor('#3b4cc0'),
            );
          }
        }
        star(g, reach, 0, 13, parseHexColor('#d9a441'));
      },
    },
    dome: {
      beam: '#14724d',
      width: 10,
      lights: ['#b8246a', '#d9a441', '#1f8a84'],
      draw(g, post, reach) {
        g.ellipse(0, -post, 16, 10).fill(parseHexColor('#b8246a'));
        g.ellipse(0, post, 16, 10).fill(parseHexColor('#1f8a84'));
        g.circle(reach, 0, 7).fill(parseHexColor('#a8781f'));
      },
    },
    ambyss: {
      beam: '#7a4fa8',
      width: 10,
      lights: ['#c8363a', '#7a4fa8', '#d9a441'],
      draw(g, post, reach) {
        const cushion = parseHexColor('#c8363a');
        g.roundRect(-10, -post - 6, 20, 12, 4).fill(cushion);
        g.roundRect(-10, post - 6, 20, 12, 4).fill(cushion);
        g.roundRect(reach - 6, -10, 12, 20, 4).fill(cushion);
      },
    },
    lake: {
      beam: '#1f8a84',
      width: 12,
      lights: ['#1f8a84', '#d9a441', '#5fd0c8'],
      draw(g, _post, reach) {
        g.poly([reach + 12, 0, reach - 8, -12, reach - 8, 12]).fill(parseHexColor('#d9a441'));
      },
    },
  } satisfies Record<string, Motif>),
);

// The gate of a stage: an arch open toward the arena, +x out, and the stage's motif.
export function drawGate(g: Graphics, decor: DecorId, radius: number): void {
  const motif = lookup(MOTIFS, decor, 'portal');
  const post = radius * 0.8;
  const reach = radius * 0.95;
  const arch = () =>
    g.moveTo(radius * 0.35, -post).quadraticCurveTo(reach * 1.6, 0, radius * 0.35, post);
  arch().stroke({ width: motif.width + 6, color: OUTLINE, cap: 'round' });
  arch().stroke({ width: motif.width, color: parseHexColor(motif.beam), cap: 'round' });
  for (const y of [-post, post]) {
    g.circle(radius * 0.35, y, motif.width * 0.7 + 3).fill(OUTLINE);
    g.circle(radius * 0.35, y, motif.width * 0.7).fill(parseHexColor(motif.beam));
  }
  motif.draw(g, post, reach);
}

export function gateLights(decor: DecorId): readonly number[] {
  return lookup(MOTIFS, decor, 'portal').lights.map(parseHexColor);
}

function star(g: Graphics, x: number, y: number, radius: number, fill: FillInput): void {
  const points: number[] = [];
  for (let i = 0; i < 8; i++) {
    const r = i % 2 === 0 ? radius : radius * 0.32;
    const angle = (i / 8) * TAU;
    points.push(x + Math.cos(angle) * r, y + Math.sin(angle) * r);
  }
  g.poly(points).fill(fill);
}

// A soft pool of colour on the ground, in rings: no glow, which would only whiten the dawn floor.
function halo(g: Graphics, x: number, y: number, radius: number, color: number, alpha: number) {
  for (const share of [1, 0.66, 0.36]) {
    g.circle(x, y, radius * share).fill({ color, alpha: alpha * 0.4 });
  }
}

function floorDisc(g: Graphics, radius: number, color: number, fill: number, stroke: number) {
  g.circle(0, 0, radius).fill({ color, alpha: fill });
  g.circle(0, 0, radius).stroke({ color, alpha: stroke, width: 3 });
}

function gauge(g: Graphics, radius: number, share: number, color: number, width: number) {
  if (share > 0) {
    g.beginPath()
      .arc(0, 0, radius, -Math.PI / 2, -Math.PI / 2 + share * TAU)
      .stroke({ color, width, cap: 'round' });
  }
}

// The lit gate: the arch 1.4 times bigger, strung with bulbs in the stage's light colours that
// chase one seat per beat, and a lamp on each post that blinks on the kick.
const LIT = { scale: 1.4, inward: 0.34, bulbs: 12, clear: 0.27, gauge: 0.73 };
const LAMP = { dim: parseHexColor('#f1cd7e'), lit: parseHexColor('#fff8e4'), halo: 0xffb63c };
const seatsByRadius = new Map<number, readonly (readonly [number, number])[]>();

function bulbSeats(radius: number): readonly (readonly [number, number])[] {
  const known = seatsByRadius.get(radius);
  if (known !== undefined) {
    return known;
  }
  const post = radius * 0.8;
  const tip = radius * 0.95 * 1.6;
  const curve: (readonly [number, number])[] = [];
  for (let i = 0; i <= 64; i++) {
    const u = i / 64;
    const ends = (1 - u) * (1 - u) + u * u;
    curve.push([ends * radius * 0.35 + 2 * u * (1 - u) * tip, post * (u * u - (1 - u) * (1 - u))]);
  }
  const along = [0];
  for (let i = 1; i < curve.length; i++) {
    const [ax, ay] = curve[i - 1] ?? [0, 0];
    const [bx, by] = curve[i] ?? [0, 0];
    along.push((along[i - 1] ?? 0) + Math.hypot(bx - ax, by - ay));
  }
  const total = along.at(-1) ?? 0;
  const clear = radius * LIT.clear;
  const seats: (readonly [number, number])[] = [];
  for (let i = 0; i < LIT.bulbs; i++) {
    const s = clear + ((total - 2 * clear) * i) / (LIT.bulbs - 1);
    const j = Math.max(
      1,
      along.findIndex((d) => d >= s),
    );
    const [ax, ay] = curve[j - 1] ?? [0, 0];
    const [bx, by] = curve[j] ?? [0, 0];
    const k = (s - (along[j - 1] ?? 0)) / ((along[j] ?? 1) - (along[j - 1] ?? 0));
    seats.push([ax + (bx - ax) * k, ay + (by - ay) * k]);
  }
  seatsByRadius.set(radius, seats);
  return seats;
}

function litGate(
  g: Graphics,
  _above: Graphics,
  decor: DecorId,
  radius: number,
  moment: ExitMoment,
) {
  floorDisc(g, radius, moment.ahead, 0.14, 0.5);
  gauge(g, radius * LIT.gauge, moment.depart, moment.gold, 6);
  g.save();
  g.translateTransform(-radius * LIT.inward, 0);
  g.scaleTransform(LIT.scale, LIT.scale);
  drawGate(g, decor, radius);
  const lights = gateLights(decor);
  const step = moment.calm ? 0 : Math.floor(moment.beats);
  bulbSeats(radius).forEach(([x, y], i) => {
    const color = lights[(i + step) % lights.length] ?? CREAM;
    halo(g, x, y, 9, color, 0.45);
    g.circle(x, y, 4).fill(OUTLINE);
    g.circle(x, y, 3).fill(color);
    g.circle(x, y, 1.2).fill(CREAM);
  });
  for (const y of [-radius * 0.8, radius * 0.8]) {
    const x = radius * 0.35;
    halo(g, x, y, 26, LAMP.halo, 0.2 + 0.6 * moment.kick);
    g.circle(x, y, 6.5).fill(OUTLINE);
    g.circle(x, y, 5).fill(LAMP.dim);
    g.circle(x, y, 5).fill({ color: LAMP.lit, alpha: moment.kick });
  }
  g.restore();
}

// The mapping mandala, drawn at the size of the review's mock-up and scaled to the exit: an outer
// ring that becomes the gauge, an eight-point star turning one way, a seed of life the other, a
// gold heart; the figures breathe on the kick.
const MANDALA = { drawn: 56, star: 10, seed: 0.3 };

function projected(g: Graphics, path: () => void, color: number, width: number, lit = 0.55) {
  for (const [alpha, tone, size] of [
    [0.22, color, width + 6],
    [0.6, OUTLINE, width + 2],
    [1, color, width],
    [lit, CREAM, width * 0.4],
  ] as const) {
    g.beginPath();
    path();
    g.stroke({ color: tone, alpha, width: size, cap: 'round', join: 'round' });
  }
}

function mandala(
  g: Graphics,
  _above: Graphics,
  decor: DecorId,
  radius: number,
  moment: ExitMoment,
) {
  const [ring = CREAM, heart = CREAM, seed = CREAM] = gateLights(decor);
  const r = MANDALA.drawn;
  const { kick, beats } = moment;
  const breath = 1 + 0.05 * kick;
  const lit = 0.45 + 0.5 * kick;
  g.save();
  g.scaleTransform(radius / r, radius / r);
  g.circle(0, 0, r).fill({ color: ring, alpha: moment.depart > 0 ? 0.15 : 0.1 });
  projected(g, () => g.circle(0, 0, r), ring, 2.6);
  if (moment.depart > 0) {
    const end = -Math.PI / 2 + moment.depart * TAU;
    projected(g, () => g.arc(0, 0, r, -Math.PI / 2, end), moment.gold, 5, 0.8);
    halo(g, Math.cos(end) * r, Math.sin(end) * r, 12, moment.gold, 0.7);
  }
  const reach = (r - MANDALA.star) * breath;
  const turned = (beats / 8) * (Math.PI / 4);
  projected(
    g,
    () => {
      for (const offset of [0, Math.PI / 4]) {
        const corners: number[] = [];
        for (let i = 0; i < 4; i++) {
          const angle = turned + offset + (i / 4) * TAU;
          corners.push(Math.cos(angle) * reach, Math.sin(angle) * reach);
        }
        g.poly(corners);
      }
    },
    ring,
    1.8,
    lit,
  );
  const back = -(beats / 8) * (Math.PI / 3);
  const petal = r * MANDALA.seed * breath;
  projected(
    g,
    () => {
      for (let i = 0; i < 6; i++) {
        const angle = back + (i / 6) * TAU;
        g.circle(Math.cos(angle) * petal, Math.sin(angle) * petal, petal);
      }
      g.circle(0, 0, petal);
    },
    seed,
    1.4,
    lit,
  );
  projected(g, () => g.circle(0, 0, petal * 2), seed, 1.8, lit);
  projected(g, () => g.circle(0, 0, 5 + 3 * kick), heart, 2.2);
  halo(g, 0, 0, 16, heart, 0.25 + 0.4 * kick);
  g.restore();
}

// Festival flags around the exit, drawn at the size of the review's mock-up: wide swallow-tailed
// silks bearing the stage's star, blowing into the arena, a gold ribbon under each and a thin one
// of the other colour over it, both rolling up at the tip; their long dawn shadows on the ground.
const FLAGS = {
  drawn: 56,
  silk: parseHexColor('#fff4e0'),
  dusk: parseHexColor('#1a0f08'),
  shade: parseHexColor('#3a2010'),
  glint: parseHexColor('#fff4d8'),
  shadow: { x: -30, y: 24 },
  notch: 9,
  planted: [
    { x: 54, y: -66, main: 0, over: 2, length: 56, phase: 0 },
    { x: -36, y: -54, main: 2, over: 0, length: 50, phase: 2.2 },
    { x: 54, y: 66, main: 2, over: 0, length: 56, phase: 3.6 },
    { x: -36, y: 54, main: 0, over: 2, length: 50, phase: 5.1 },
  ],
} as const;

interface RibbonPoint {
  x: number;
  y: number;
  half: number;
  fold: number;
  nx: number;
  ny: number;
}

interface RibbonShape {
  readonly length: number;
  readonly from: number;
  readonly to: number;
  readonly wave: number;
  readonly phase: number;
  readonly offset: number;
  readonly curl?: number;
}

// A silk seen from the sky, +x downwind: its rippling centreline, the wave growing toward the free
// end, then the turns of a spiral rolling up its tip.
function ribbon(shape: RibbonShape, beats: number): RibbonPoint[] {
  const points: RibbonPoint[] = [];
  const steps = 30;
  for (let i = 0; i <= steps; i++) {
    const s = i / steps;
    const fold = ((s * shape.length) / 30) * TAU - beats * TAU * 0.55 + shape.phase;
    points.push({
      x: s * shape.length,
      y: shape.offset + shape.wave * (0.5 + 5 * s) * Math.sin(fold),
      half: (shape.from + (shape.to - shape.from) * s) / 2,
      fold,
      nx: 0,
      ny: 0,
    });
  }
  const end = points.at(-1);
  if (shape.curl !== undefined && end !== undefined) {
    const size = Math.abs(shape.curl * 6);
    const way = Math.sign(shape.curl);
    for (let i = 1; i <= 22; i++) {
      const u = i / 22;
      const angle = (-way * Math.PI) / 2 + way * u * TAU * 1.35;
      const r = size * (1 - 0.75 * u);
      points.push({
        x: end.x + Math.cos(angle) * r,
        y: end.y + way * size + Math.sin(angle) * r,
        half: (shape.to / 2) * (1 - 0.6 * u),
        fold: Math.PI / 2,
        nx: 0,
        ny: 0,
      });
    }
  }
  points.forEach((point, i) => {
    const a = points[Math.max(0, i - 1)] ?? point;
    const b = points[Math.min(points.length - 1, i + 1)] ?? point;
    const span = Math.hypot(b.x - a.x, b.y - a.y) || 1;
    point.nx = -(b.y - a.y) / span;
    point.ny = (b.x - a.x) / span;
  });
  return points;
}

function outline(points: readonly RibbonPoint[], notch: number): number[] {
  const flat: number[] = [];
  for (const p of points) {
    flat.push(p.x + p.nx * p.half, p.y + p.ny * p.half);
  }
  const end = points.at(-1);
  if (notch > 0 && end !== undefined) {
    flat.push(end.x - notch, end.y);
  }
  for (let i = points.length - 1; i >= 0; i--) {
    const p = points[i];
    if (p !== undefined) {
      flat.push(p.x - p.nx * p.half, p.y - p.ny * p.half);
    }
  }
  return flat;
}

function silk(g: Graphics, points: readonly RibbonPoint[], color: number, banner: boolean) {
  const notch = banner ? FLAGS.notch : 0;
  g.poly(outline(points, notch)).fill(color);
  const shaded = (points.at(-1)?.x ?? 0) - notch;
  for (let i = 1; i < points.length; i++) {
    const a = points[i - 1];
    const b = points[i];
    if (a === undefined || b === undefined || b.x > shaded) {
      continue;
    }
    const fold = Math.cos(b.fold);
    const tone =
      fold < 0 ? mixColor(color, FLAGS.dusk, -fold * 0.4) : mixColor(color, FLAGS.silk, fold * 0.2);
    g.poly([
      a.x + a.nx * a.half,
      a.y + a.ny * a.half,
      b.x + b.nx * b.half,
      b.y + b.ny * b.half,
      b.x - b.nx * b.half,
      b.y - b.ny * b.half,
      a.x - a.nx * a.half,
      a.y - a.ny * a.half,
    ]).fill(tone);
  }
  if (banner) {
    for (const side of [-0.55, 0.55]) {
      g.beginPath();
      points.forEach((p, i) => {
        const x = p.x + p.nx * p.half * side;
        const y = p.y + p.ny * p.half * side;
        if (i === 0) {
          g.moveTo(x, y);
        } else {
          g.lineTo(x, y);
        }
      });
      g.stroke({ color: FLAGS.silk, alpha: 0.6, width: 0.6 });
    }
  }
  g.poly(outline(points, notch)).stroke({ color: OUTLINE, width: 1.2, join: 'round' });
}

// The silks fly above the players and the decor; the disc, the gauge and the shadows lie on the ground.
function flags(
  g: Graphics,
  above: Graphics,
  decor: DecorId,
  radius: number,
  moment: ExitMoment,
): void {
  const lights = gateLights(decor);
  const gold = lights[1] ?? CREAM;
  const { beats } = moment;
  floorDisc(g, radius, moment.ahead, 0.16, 0.55);
  if (moment.depart > 0) {
    g.circle(0, 0, radius - 1.4).stroke({ color: moment.gold, alpha: 0.3, width: 7 });
    gauge(g, radius - 1.4, moment.depart, moment.gold, 7);
  }
  g.save();
  g.scaleTransform(radius / FLAGS.drawn, radius / FLAGS.drawn);
  const cos = Math.cos(-moment.turn);
  const sin = Math.sin(-moment.turn);
  const shadow = {
    x: FLAGS.shadow.x * cos - FLAGS.shadow.y * sin,
    y: FLAGS.shadow.x * sin + FLAGS.shadow.y * cos,
  };
  const banners = FLAGS.planted.map((flag) =>
    ribbon({ length: flag.length, from: 20, to: 14, wave: 1, phase: flag.phase, offset: 0 }, beats),
  );
  FLAGS.planted.forEach((flag, i) => {
    g.moveTo(flag.x, flag.y)
      .lineTo(flag.x + shadow.x, flag.y + shadow.y)
      .stroke({ color: FLAGS.shade, alpha: 0.12, width: 2.4 });
    g.save();
    g.translateTransform(flag.x + shadow.x, flag.y + shadow.y);
    g.rotateTransform(Math.PI);
    g.poly(outline(banners[i] ?? [], FLAGS.notch)).fill({ color: FLAGS.shade, alpha: 0.12 });
    g.restore();
  });
  g.restore();
  above.save();
  above.scaleTransform(radius / FLAGS.drawn, radius / FLAGS.drawn);
  const glint = moment.depart > 0 ? moment.kick * 0.8 : 0;
  FLAGS.planted.forEach((flag, i) => {
    const main = banners[i] ?? [];
    const under = ribbon(
      {
        length: flag.length + 12,
        from: 9,
        to: 5,
        wave: 1.1,
        phase: flag.phase - 0.7,
        offset: -7,
        curl: -1,
      },
      beats,
    );
    const over = ribbon(
      {
        length: flag.length + 4,
        from: 5,
        to: 3.4,
        wave: 1.2,
        phase: flag.phase - 1.3,
        offset: 8,
        curl: 0.8,
      },
      beats,
    );
    above.save();
    above.translateTransform(flag.x, flag.y);
    above.rotateTransform(Math.PI);
    silk(above, under, gold, false);
    silk(above, main, lights[flag.main] ?? CREAM, true);
    silk(above, over, lights[flag.over] ?? CREAM, false);
    const emblem = main[7];
    if (emblem !== undefined) {
      star(above, emblem.x, emblem.y + 1, 9, OUTLINE);
      star(above, emblem.x, emblem.y + 1, 7.4, gold);
      if (glint > 0) {
        star(above, emblem.x, emblem.y + 1, 7.4, { color: FLAGS.glint, alpha: glint });
      }
    }
    above.restore();
    above.circle(flag.x, flag.y, 3.8).fill(gold).stroke({ color: OUTLINE, width: 1.4 });
    above.circle(flag.x - 0.8, flag.y - 0.8, 1.2).fill(FLAGS.silk);
  });
  above.restore();
}

type Look = (
  g: Graphics,
  above: Graphics,
  decor: DecorId,
  radius: number,
  moment: ExitMoment,
) => void;

// Each stage ahead has its own exit (review of 2026-10-28).
const LOOKS = new Map<DecorId, Look>([
  ['pumpui', flags],
  ['dome', mandala],
  ['ambyss', litGate],
  ['lake', litGate],
]);

// `g` lies on the ground, `above` over the players.
export function drawExitLook(
  g: Graphics,
  above: Graphics,
  decor: DecorId,
  radius: number,
  moment: ExitMoment,
): void {
  lookup(LOOKS, decor, 'exit look')(g, above, decor, radius, moment);
}
